'use strict'
import L from 'leaflet';
import AddressService from './address-service';
import bingTileLayer from 'leaflet-bing-layer';
import { vectorTileLayer } from 'esri-leaflet-vector';

var esri = require('esri-leaflet');

const MyPolylineEditor = L.Editable.PolylineEditor.extend({
    onFeatureAdd: function () {
        this.tools.editLayer.addLayer(this.editLayer);
        if (this.feature.dragging) this.feature.dragging.disable();
    },
});

const MyPolygonEditor = L.Editable.PolygonEditor.extend({
    onFeatureAdd: function () {
        this.tools.editLayer.addLayer(this.editLayer);
        if (this.feature.dragging) this.feature.dragging.disable();
    },
});

export default class Map extends L.Map {
    constructor(
        renderTo,
        settings = {
            initPhotosWindow: false,
            layerName: 'OpenStreetMap',
            maplayer: 'OpenStreetMap',
            mapCenter: [58.028020, 56.301578],
            zoom: 12,
            showObjectsNames: true,
            geofenceTooltip: { isAlwaysVisible: true, minVisibleZoom: 12 },
            rosreestrOverlay: { isVisible: false, layer: null },
            isRadar: false,
            isMirepTrips: false,
            maxZoom: 19
    }) {
        super(renderTo, {
            center: settings.mapCenter || [58.028020, 56.301578],
            zoom: settings.zoom || 12,
            initPhotosWindow: settings.initPhotosWindow || false,
            initPlayback: settings.initPlayback || false,
            maxZoom: settings.maxZoom || 19,
            zoomAnimation: true,
            fadeAnimation: false,
            renderer: L.canvas(),
            showObjectsNames: settings.showObjectsNames,
            showObjectIconDirection: settings.showObjectIconDirection,
            geofenceTooltip: {
                isAlwaysVisible: settings.geofenceTooltip && settings.geofenceTooltip.isAlwaysVisible || false,
                minVisibleZoom: settings.geofenceTooltip && settings.geofenceTooltip.minVisibleZoom || 12
            },
            rosreestrOverlay: {
                isVisible: settings.rosreestrOverlay && settings.rosreestrOverlay.isVisible || false,
                cadDivisionLayer: null,
                annoLayer: null,
                tiledLayer: null
            },
            inertia: false,
            zoomControl: false,
            preferCanvas: true,
            markerZoomAnimation: false,
            doubleClickZoom: false,
            measureControl: true,
            editable: true,
            editOptions: {
                polylineEditorClass: MyPolylineEditor,
                polygonEditorClass: MyPolygonEditor,
            },
        });
        this.selectedLayer = null;
        this.yandexScriptLoaded = false;
        this.isEditingGeofence = false;
        this.baseLayers = [];
        this.initPanes();
        this.initBaseLayers();
        this.setSelectedLayer(settings);
        this.on('zoomend', (e) => {
            let event = new CustomEvent('zoomend');
            document.dispatchEvent(event);
        });
        this.setBaseLayer.bind(this);
        document.addEventListener('map:fitBounds', e => this.fitBounds(e.detail.bounds));
        this.isReport = settings.isReport === true ? true : false;
        this.isMirepTrips = !!settings.isMirepTrips;
        this.isRadar = !!settings.isRadar;
        this.maxZoom = settings.maxZoom || 19;

    }

    initMeasure() {
        this.measure = L.control.polylineMeasure({
            position: 'topleft',
            imperial: false,
        }).addTo(this);
    }

    initAddressService() {
        this.addressService = new AddressService(this);
    }

    toggleObjectsNamesVisibility = () => {
        this.options.showObjectsNames = !this.options.showObjectsNames;
        const event = new CustomEvent('map:toggleObjectsNamesVisibility');
        document.dispatchEvent(event);
        return this.options.showObjectsNames;
    }

    toggleGeofenceTooltipVisibility = () => {
        this.options.geofenceTooltip.isAlwaysVisible = !this.options.geofenceTooltip.isAlwaysVisible;
        let event = new CustomEvent('map:toggleGeofenceTooltipVisibility');
        document.dispatchEvent(event);
        return this.options.geofenceTooltip.isAlwaysVisible;
    }

    toggleRosreestrOverlayVisibility = () => {
        this.options.rosreestrOverlay.isVisible = !this.options.rosreestrOverlay.isVisible;

        if (this.options.rosreestrOverlay.isVisible) {
            this.options.rosreestrOverlay.cadDivisionLayer = vectorTileLayer("https://pkk.rosreestr.ru/arcgis/rest/services/Hosted/caddivsion/VectorTileServer");
            this.options.rosreestrOverlay.annoLayer = vectorTileLayer("https://pkk.rosreestr.ru/arcgis/rest/services/Hosted/vt_anno_light/VectorTileServer");

            let tileLayerUrl = "https://pkk.rosreestr.ru/arcgis/rest/services/PKK6/CadastreObjects/MapServer";

            this.options.rosreestrOverlay.tiledLayer = esri.tiledDynamicMapLayer(tileLayerUrl, {
                layers: [30, 27, 24, 23, 22],
                bboxSR: 102100,
                imageSR: 102100,
                format: 'PNG32',
                transparent: true,
                f: 'image',
                tiled: true,
                zoom: 8,
                tileSize: 1024,
                url: tileLayerUrl
            });
            
            this.addLayer(this.options.rosreestrOverlay.cadDivisionLayer);
            this.addLayer(this.options.rosreestrOverlay.annoLayer);
            this.addLayer(this.options.rosreestrOverlay.tiledLayer);
        }
        else {
            if (this.options.rosreestrOverlay.cadDivisionLayer != null)
                this.mapRemoveLayer(this.options.rosreestrOverlay.cadDivisionLayer);

            if (this.options.rosreestrOverlay.annoLayer != null)
                this.mapRemoveLayer(this.options.rosreestrOverlay.annoLayer);

            if (this.options.rosreestrOverlay.tiledLayer != null)
                this.mapRemoveLayer(this.options.rosreestrOverlay.tiledLayer);
        };

        return this.options.rosreestrOverlay.isVisible;
    }

    initBaseLayers() {
        let necessaryLayers = this.identifyNecessaryLayers();
        for (let layerName of necessaryLayers) {
            this.initLayer(layerName);
        }
    }

    setSelectedLayer(settings) {
        let baseLayer;

        if (settings.mapBaseLayer) {
            baseLayer = this.baseLayers.find(elem => elem.name === settings.mapBaseLayer);
        }

        if (!baseLayer) {
            baseLayer = this.baseLayers[0];
        }

        if (baseLayer && baseLayer.layer.isYandex && !this.yandexScriptLoaded) {
            baseLayer = this.reinitYandexLayer(baseLayer.name);
        }
        else {
            this.setSelectedBaseLayer(baseLayer);
        }
    }

    setSelectedBaseLayer(baseLayer) {
        baseLayer.layer.off('tileload');

        try {
            this.selectedLayer = baseLayer;
            this.addLayer(baseLayer.layer);
        }
        catch (err) {
            console.error(err);
            baseLayer = this.baseLayers[0];
            this.selectedLayer = baseLayer;
            this.addLayer(baseLayer.layer);
        }

        baseLayer.layer.on('tileload', function () {
            this.tilesCount++;
        });
    }

    identifyNecessaryLayers() {
        let necessaryLayers = ["OpenStreetMap"];

        try {
            if (needYandexMaps) {
                necessaryLayers  = necessaryLayers.concat(["Yandex Map", "Yandex Traffic"]);
                if(!window.YandexKey) {
                    necessaryLayers  = necessaryLayers.concat(["Yandex Satellite", "Yandex Hybrid"]);
                }
            }
        }
        catch(error) {
            console.error('Yandex map', error);
        }

        try {
            if (needWikimapiaMaps) {
                necessaryLayers.push("Wikimapia");
            }

            if (needGoogleMaps && GoogleKey.length > 0) {
                necessaryLayers = necessaryLayers.concat(["Google Map", "Google Satellite", "Google Hybrid", "Google Terrain", "Google Traffic"]);
            }

            if (need2GISMaps) {
                necessaryLayers.push("2GIS Map");
            }

            if (needGISServerMaps) {
                necessaryLayers.push("GIS Server Map");
            }

            if (window.needVisicom) {
                necessaryLayers.push("Visicom");
            }

            if (needBingMap && bingKey.length > 0) {
                necessaryLayers = necessaryLayers.concat(["Bing Map", "Bing Satellite", "Bing Hybrid"]);
            }

            try {
                if (needHereMap && hereAppId.length > 0 && hereAppCode.length > 0) {
                    necessaryLayers = necessaryLayers.concat(["Here Map", "Here Satellite", "Here Hybrid"]);
                }
            }
            catch (err) {
                console.error('HERE maps', err);
            }

            if (needVolgogradWMS) {
                necessaryLayers.push("Атлас ВО");
            }

            try {
                if (typeof OSMLocalUrl !== 'undefined' && OSMLocalUrl.length > 10) {
                    necessaryLayers.push("OSM Local");
                }
            }
            catch (err) {
                console.error('OSM Local map', err);
            }

            if (needKosmosnimkiMaps) {
                necessaryLayers.push("Kosmosnimki");
            }
        }
        catch(error) {
            console.error('Map layers', error);
        }
        return necessaryLayers;
    }

    initPanes() {
        this.createPane('zonesPane').style.zIndex = 609;
        this.createPane('objectsPane').style.zIndex = 610;
        this.createPane('routePane').style.zIndex = 611;
        this.createPane('routeElementsPane').style.zIndex = 612;
        this.createPane('startEndRoutePane').style.zIndex = 660;
    }

    isClickBlocked() {
        return this.isRadar
            || this.isEditingGeofence
            || this.addressService.enable
            || this.measure._measuring;
    }

    isAllClicksBlocked() {
        return this.isMirepTrips || this.isClickBlocked();
    }

    initLayer(layerName) {
        let layer;

        let currenCulture = "ru-ru";

        try {
            if (window.hasOwnProperty('culture'))
                currenCulture = culture;
        }
        catch(ex) {
            console.error(ex);
        }

        switch (layerName) {
            case "OpenStreetMap":
                let url;
                try {
                    url = OSMGlobalUrl || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
                }
                catch {
                    url = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
                }
                layer = new L.TileLayer(
                    url,
                    {
                        minZoom: 0,
                        maxZoom: 19,
                        //attribution: 'Map data <a href="//openstreetmap.org">OpenStreetMap</a> contributors',
                        maxNativeZoom: 19,
                        //maxZoom: this.maxZoom
                    }
                );
                break;
            case "OSM Local":
                // вот здесь осталась глобальная переменная OSMLocalUrl !!!
                layer = new L.TileLayer(
                    OSMLocalUrl,
                    {
                        minZoom: 0,
                        //maxZoom: this.maxZoom,
                        maxZoom: 19,
                        maxNativeZoom: 19,
                    }
                );
                break;
            case "Yandex Map":
                layer = new L.Yandex("null");
                layer.isYandex = true;
                break;
            case "Yandex Traffic":
                layer = new L.Yandex("null", { traffic: true, opacity: 0.8, overlay: true });
                layer.isYandex = true;
                break;
            case "Yandex Satellite":
                layer = new L.Yandex('satellite');
                layer.isYandex = true;
                break;
            case "Yandex Hybrid":
                layer = new L.Yandex('hybrid');
                layer.isYandex = true;
                break;
            case "Wikimapia":
                layer = L.tileLayer('//{s}{hash}.wikimapia.org/?x={x}&y={y}&zoom={z}&r=7071412&type=&lng=1', {
                    hash: function (data) {
                        return data.x % 4 + (data.y % 4) * 4;
                    },
                    subdomains: 'i',
                    //maxZoom: this.maxZoom,
                    maxZoom: 19,
                    maxNativeZoom: 19,
                    //attribution: '&lt;a href="http://wikimapia.org" target="_blank"&gt;Wikimapia.org&lt;/a&gt;'
                });
                break;
            case "Google Map":
                layer = new L.gridLayer.googleMutant({ type: 'roadmap', maxNativeZoom: 19, maxZoom: 19/*maxZoom: this.maxZoom*/, styles: [] });
                break;
            case "Google Satellite":
                layer = new L.gridLayer.googleMutant({ type: 'satellite', maxNativeZoom: 19, maxZoom: 19/*maxZoom: this.maxZoom*/, styles: [] });
                break;
            case "Google Hybrid":
                layer = new L.gridLayer.googleMutant({ type: 'hybrid', maxNativeZoom: 19, maxZoom: 19/*maxZoom: this.maxZoom*/, styles: [] });
                break;
            case "Google Terrain":
                layer = new L.gridLayer.googleMutant({ type: 'terrain', maxNativeZoom: 19, maxZoom: 19/*maxZoom: this.maxZoom*/, styles: [] });
                break;
            case "Google Traffic":
                layer = new L.gridLayer.googleMutant({ type: 'roadmap', trafficLayer: true, maxNativeZoom: 19, maxZoom: 19/*maxZoom: this.maxZoom*/, styles: [] });
                break;
            case "2GIS Map":
                layer = new L.TileLayer(
                    '//tile1.maps.2gis.com/tiles?x={x}&y={y}&z={z}',
                    { minZoom: 0, maxZoom: 18/*maxZoom: this.maxZoom*/, maxNativeZoom: 18, attribution: '<a href="//www.2gis.com">2GIS Map</a>' }
                );
                break;
            case "GIS Server Map":
                // вот здесь осталась глобальная переменная GISServerUrl !!!
                layer = new L.TileLayer(
                    GISServerUrl + "/MapServer/default.aspx?x={x}&y={y}&z={z}",
                    { minZoom: 0, maxZoom: 20/*maxZoom: this.maxZoom*/, maxNativeZoom: 20 }
                );
                break;
            case "Visicom":
                layer = new L.TileLayer('//tms{s}.visicom.ua/2.0.0/planet3/base_ru/{z}/{x}/{y}.png', {
                    maxZoom: 19/*maxZoom: this.maxZoom*/,
                    maxNativeZoom: 19,
                    tms: true,
                    //attribution: 'Данные компании © 2017 <a href="http://visicom.ua/">Визиком</a>',
                    subdomains: '123'
                });
                break;
            case "Bing Map":
                // вот здесь осталась глобальная переменная culture !!!
                layer = new bingTileLayer({
                    bingMapsKey: bingKey,
                    imagerySet: 'RoadOnDemand',
                    culture: currenCulture,
                    maxZoom: 19/*maxZoom: this.maxZoom*/,
                    maxNativeZoom: 19
                });
                break;
            case "Bing Satellite":
                layer = new bingTileLayer({
                    bingMapsKey: bingKey,
                    imagerySet: 'Aerial',
                    culture: currenCulture,
                    maxNativeZoom: 19,
                    maxZoom: 19/*maxZoom: this.maxZoom*/
                });
                break;
            case "Bing Hybrid":
                layer = new bingTileLayer({
                    bingMapsKey: bingKey,
                    imagerySet: 'AerialWithLabelsOnDemand',
                    culture: currenCulture,
                    maxNativeZoom: 19,
                    maxZoom: 19/*maxZoom: this.maxZoom*/
                });
                break;
            case "Here Map":
                layer = new L.tileLayer.here({ appId: hereAppId, appCode: hereAppCode, scheme: 'normal.day', maxNativeZoom: 19, maxZoom: 19/*maxZoom: this.maxZoom*/ });
                break;
            case "Here Satellite":
                layer = new L.tileLayer.here({ appId: hereAppId, appCode: hereAppCode, scheme: 'satellite.day', maxNativeZoom: 19, maxZoom: 19/*maxZoom: this.maxZoom*/ });
                break;
            case "Here Hybrid":
                layer = new L.tileLayer.here({ appId: hereAppId, appCode: hereAppCode, scheme: 'hybrid.day', maxNativeZoom: 19, maxZoom: 19/*maxZoom: this.maxZoom*/ });
                break;
            case "Атлас ВО":
                layer = new L.tileLayer.wms('http://maps.citvo.ru/arcgis/services/topo_schema_101/MapServer/WMSServer?', {
                    layers: '0,1,2,3,5,6,7,9,10,11,12,14,15,16,17,18,19,21,22,23,24,26,27,28,29,30,31,32,34,35,36,37,38,39,40,41,42',
                    format: 'image/png',
                    request: 'GetMap',
                    srs: 'EPSG:4326',
                    //attribution: 'ГБУ "ЦИТ ВО"'
                });
                break;
            case "Kosmosnimki":
                layer = new L.TileLayer(
                    'https://maps.kosmosnimki.ru/TileSender.ashx?ModeKey=tile&ftc=osm&z={z}&x={x}&y={y}&srs=3857&sw=1&LayerName=4EE5E84381E94E369784464FD41EED5A&key=' + kosmosnimkiKey,
                    {
                        minZoom: 3,
                        //attribution: 'Map data <a href="https://maps.kosmosnimki.ru/">Kosmosnimki</a>',
                        maxNativeZoom: 16,
                        maxZoom: 16/*maxZoom: this.maxZoom*/
                    }
                );
                break;
            default:
                break;
        }

        layer.tilesCount = 0;

        layer.on('tileload', function () {
            layer.tilesCount++;
        });

        var existLayer = this.baseLayers.find(elem => elem.name === layerName);
        if (existLayer) {
            existLayer.layer = layer;
        }
        else {
            this.baseLayers.push({
                name: layerName,
                layer: layer
            });
        }
    }

    reinitYandexLayer(layerName) {

        this.loadYandexScript().then(() => {
            this.yandexScriptLoaded = true;
            this.initLayer(layerName);

            var baseLayer = this.baseLayers.find(elem => elem.name === layerName);

            this.setSelectedBaseLayer(baseLayer);
        });
    }

    getTilesStatistic() {
        var tilesLoadStat = {
            mapTiles: '',
            allTiles: 0
        };

        var layersInfo = []

        for (i = 0; i < this.baseLayers.length; i++) {
            if (this.baseLayers[i].layer.tilesCount > 0) {
                layersInfo.push(this.baseLayers[i].name + ': ' + this.baseLayers[i].layer.tilesCount);

                tilesLoadStat.allTiles += this.baseLayers[i].layer.tilesCount;

                this.baseLayers[i].layer.tilesCount = 0;
            }
        }

        tilesLoadStat.mapTiles = layersInfo.toString();

        return tilesLoadStat;
    }

    setBaseLayer = baseLayer => {
        this.selectedLayer.layer.off('tileload');

        this.mapRemoveLayer(this.selectedLayer.layer);

        if (baseLayer.layer.isYandex && !this.yandexScriptLoaded) {
            baseLayer = this.reinitYandexLayer(baseLayer.name);
        }
        else {
            this.setSelectedBaseLayer(baseLayer);
        }
    }

    mapRemoveLayer(layer) {
        if (this.hasLayer(layer)) {
            this.removeLayer(layer);
        };
    }

    setMapCenterByGeolocation () {
        try {
            const setMapCenterByIP = () => {
                try {
                    fetch('http://ip-api.com/json')
                    .then(response => {
                        if (response.status == 200) {
                            return response.json();
                        } else {
                            let error = new Error(response.statusText);
                            error.response = response;
                            throw error                        
                        }
                    })
                    .then( response => {
                        this.setView([response.lat, response.lon]);
                    })
                    .catch(error => {
                        console.error('Request failed.  Returned status of', status);
                    });
                }
                catch (err) {
                    console.error('setMapCenterByIP error', err);
                }
            }

            if (!navigator.geolocation) {
                console.error("Geolocation is not supported by your browser");
                setMapCenterByIP();
                return;
            }

            const success = (position) => {
                let latitude = position.coords.latitude,
                    longitude = position.coords.longitude;

                this.setView([latitude, longitude]);
            };

            const error = (err) => {
                console.error("Unable to retrieve your location", err);
                setMapCenterByIP();
            };

            navigator.geolocation.getCurrentPosition(success, error);
        }
        catch (err) {
            console.error('setMapCenterByGeo', err);
        }
    }

    loadYandexScript() {
        var url = '';

        if (YandexKey) {
            url = 'https://enterprise.api-maps.yandex.ru/2.0/?load=package.map&lang=ru-RU&apikey=' + YandexKey;
        } else {
            url = 'https://api-maps.yandex.ru/2.0/?load=package.map&lang=ru-RU';
        }

        return new Promise((resolve, reject) => {
            var script = document.createElement('script');
            script.src = url;
            script.onload = () => {
                resolve();
            }
            script.onerror = () => {
                reject('cannot load script ' + url);
            }

            document.body.appendChild(script);
        });
    }
}
